import React, { useState, useMemo, useEffect } from 'react';
import dataManager from '../utils/dataManager';
import YogaPoseImage from './YogaPoseImage';

const Dashboard = ({ setActiveView }) => {
  const [selectedPoseCategory, setSelectedPoseCategory] = useState('');
  const [expandedPose, setExpandedPose] = useState(null);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [reflectionsData, setReflectionsData] = useState([]);
  const [poseCategories, setPoseCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 广告图片数组
  const adImages = [
    '/static/yoga/Ad1.webp',
    '/static/yoga/Ad2.webp',
    '/static/yoga/Ad3.webp'
  ];

  // 加载体式反思数据和体式数据
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 并行加载体式反思数据和体式数据
        const [reflections, poses] = await Promise.all([
          dataManager.getPracticeReflections(),
          dataManager.getPoses()
        ]);

        setReflectionsData(Array.isArray(reflections) ? reflections : []);

        setPoseCategories(poses && typeof poses === 'object' ? poses : {});

        // 调试信息
        console.log('📊 体式反思数据:', reflections);
        console.log('📊 体式反思数据数量:', Array.isArray(reflections) ? reflections.length : 0);
        if (Array.isArray(reflections) && reflections.length > 0) {
          const firstReflection = reflections[0];
          console.log('📊 第一个体式反思数据:', firstReflection);
          console.log('📊 体式反思内容:', firstReflection.content);
          console.log('📊 体式反思内容类型:', typeof firstReflection.content);
          console.log('📊 体式反思内容长度:', firstReflection.content?.length);
          console.log('📊 体式反思用户:', firstReflection.user);
          console.log('📊 体式反思体式:', firstReflection.focused_poses || firstReflection.focusedPoses);
          console.log('📊 所有字段:', Object.keys(firstReflection));

          // 检查前几个体式反思的内容
          reflections.slice(0, 3).forEach((refl, index) => {
            console.log(`📊 体式反思${index + 1}内容:`, refl.content, `(类型: ${typeof refl.content})`);
            console.log(`📊 体式反思${index + 1}体式:`, refl.focused_poses || refl.focusedPoses);
          });
        } else {
          console.warn('⚠️ 没有体式反思数据或数据格式错误');
        }
        console.log('📊 体式数据结构:', poses);
        console.log('📊 体式分类:', Object.keys(poses || {}));

        // 检查体式数据的分类
        if (poses && typeof poses === 'object') {
          Object.keys(poses).forEach(category => {
            console.log(`📊 分类 "${category}" 包含 ${poses[category]?.length || 0} 个体式`);
            if (poses[category] && poses[category].length > 0) {
              console.log(`📊 "${category}" 第一个体式:`, poses[category][0]);
            }
          });
        }

        // 设置默认选中的分类
        const categories = Object.keys(poses || {});
        console.log('📊 可用分类列表:', categories);

        // 优先选择瑜伽分类，如果没有则选择第一个
        const preferredCategories = ['拜日式', '站立体式', '坐立体式', '后弯体式', '结束体式'];
        let defaultCategory = categories.find(cat => preferredCategories.includes(cat));

        if (!defaultCategory && categories.length > 0) {
          defaultCategory = categories[0];
        }

        if (defaultCategory && !selectedPoseCategory) {
          setSelectedPoseCategory(defaultCategory);
          console.log('📊 默认选中分类:', defaultCategory);
        }

      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('加载数据失败，请确保Django服务器正在运行');
        setReflectionsData([]);
        setPoseCategories({}); // 服务器模式失败时设置为空对象
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // 轮播图自动切换
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAdIndex((prevIndex) => (prevIndex + 1) % adImages.length);
    }, 4000); // 每4秒切换一次

    return () => clearInterval(interval);
  }, [adImages.length]);

  // 体式分类数据现在从服务端或本地配置获取（通过状态管理）

  const categoryTabs = Object.keys(poseCategories || {});

  // 获取体式图片的函数，优先使用数据库中的图片路径
  const getPoseImage = (pose, poseName = null) => {
    console.log('🖼️ 获取图片 - 输入:', pose, '体式名:', poseName);

    // 如果传入的是体式对象且有图片路径
    if (typeof pose === 'object' && pose.image_path) {
      console.log('🖼️ 使用 image_path:', pose.image_path);
      return pose.image_path;
    }

    // 如果传入的是体式对象且有图片文件名
    if (typeof pose === 'object' && pose.image_name) {
      const imagePath = `/static/yoga/${pose.image_name}`;
      console.log('🖼️ 使用 image_name:', imagePath);
      return imagePath;
    }

    // 如果传入的是体式名称字符串，尝试在所有分类中查找对应的体式对象
    const searchName = poseName || (typeof pose === 'string' ? pose : null);
    if (searchName) {
      console.log('🖼️ 搜索体式名称:', searchName);

      // 在所有分类中搜索
      for (const [category, poses] of Object.entries(poseCategories)) {
        if (Array.isArray(poses)) {
          const poseObj = poses.find(p => {
            const matches = p.name === searchName ||
                          p.chinese_name === searchName ||
                          p.chineseName === searchName ||
                          p.sanskritName === searchName ||
                          p.sanskrit_name === searchName;
            if (matches) {
              console.log('🖼️ 找到匹配体式:', p);
            }
            return matches;
          });

          if (poseObj) {
            if (poseObj.image_path) {
              console.log('🖼️ 使用找到的 image_path:', poseObj.image_path);
              return poseObj.image_path;
            }
            if (poseObj.image_name) {
              const imagePath = `/static/yoga/${poseObj.image_name}`;
              console.log('🖼️ 使用找到的 image_name:', imagePath);
              return imagePath;
            }
          }
        }
      }

      // 如果没找到，尝试根据体式名称推断图片
      const imageMap = getPoseImageByName(searchName);
      if (imageMap) {
        console.log('🖼️ 使用名称映射:', imageMap);
        return imageMap;
      }
    }

    // 默认图片
    console.log('🖼️ 使用默认图片');
    return '/static/yoga/All.png';
  };

  // 根据体式名称获取图片的映射函数（与config.json保持一致）
  const getPoseImageByName = (poseName) => {
    const imageMap = {
      // 拜日式
      '太阳礼拜A': '/static/yoga/A.png',
      '太阳礼拜B': '/static/yoga/B.png',

      // 站立体式 (按正确的阿斯汤加顺序)
      '站立前屈手抓大脚趾式': '/static/yoga/站立前驱.png',        // 1. Padangusthasana
      '站立前屈脚踩手掌式': '/static/yoga/站立前驱.png',          // 2. Padahastasana
      '三角伸展式A': '/static/yoga/三角式.png',                  // 3. Utthita Trikonasana A
      '三角伸展式B': '/static/yoga/三角式.png',                  // 4. Utthita Trikonasana B
      '侧角伸展式A': '/static/yoga/侧角.png',                    // 5. Utthita Parsvakonasana A
      '侧角伸展式B': '/static/yoga/侧角.png',                    // 6. Utthita Parsvakonasana B
      '双角式A': '/static/yoga/双角.png',                        // 7. Prasarita Padottanasana A
      '双角式B': '/static/yoga/双角.png',                        // 8. Prasarita Padottanasana B
      '双角式C': '/static/yoga/双角.png',                        // 9. Prasarita Padottanasana C
      '双角式D': '/static/yoga/双角.png',                        // 10. Prasarita Padottanasana D
      '加强背部伸展式': '/static/yoga/单腿加强背.png',            // 11. Parsvottanasana (先)
      '手拉脚单腿直立式A': '/static/yoga/手拉脚单腿直立A.png',     // 12. Utthita Hasta Padangusthasana A (再)
      '手拉脚单腿直立式B': '/static/yoga/手拉脚单腿直立B.png',     // 13. Utthita Hasta Padangusthasana B
      '手拉脚单腿直立式C': '/static/yoga/手拉脚单腿直立C.png',     // 14. Utthita Hasta Padangusthasana C
      '半莲花单腿加强背部伸展式': '/static/yoga/站立半莲花前驱.png', // 15. Ardha Baddha Padmottanasana (最后)
      '幻椅式': '/static/yoga/幻影.png',                          // 16. Utkatasana
      '战士一式': '/static/yoga/战士一二.png',                    // 17. Virabhadrasana I
      '战士二式': '/static/yoga/战士一二.png',                    // 18. Virabhadrasana II

      // 坐立体式
      '棍棒式': '/static/yoga/手杖.png',
      '西方背部伸展式A': '/static/yoga/西方伸展.png',
      '西方背部伸展式B': '/static/yoga/西方伸展.png',
      '西方背部伸展式C': '/static/yoga/西方伸展.png',
      '东方伸展式': '/static/yoga/东方伸展.png',
      '半莲花前屈加强背部伸展式': '/static/yoga/半莲花前驱.png',
      '半英雄前屈加强背部伸展式': '/static/yoga/半英雄.png',
      '头碰膝单腿加强背部伸展式A': '/static/yoga/头碰膝ABC.png',
      '头碰膝单腿加强背部伸展式B': '/static/yoga/头碰膝ABC.png',
      '头碰膝单腿加强背部伸展式C': '/static/yoga/头碰膝ABC.png',
      '圣哲玛里琪A': '/static/yoga/玛利奇ABCD.png',
      '圣哲玛里琪B': '/static/yoga/玛利奇ABCD.png',
      '圣哲玛里琪C': '/static/yoga/玛利奇ABCD.png',
      '圣哲玛里琪D': '/static/yoga/玛利奇ABCD.png',
      '船式': '/static/yoga/船式.png',
      '双臂交叉支撑式': '/static/yoga/压臂式.png',
      '龟式': '/static/yoga/龟式.png',
      '睡龟式': '/static/yoga/卧龟式.png',
      '子宫胎儿式': '/static/yoga/胎儿式.png',
      '公鸡式': '/static/yoga/公鸡式.png',
      '束角式A': '/static/yoga/束角式.png',
      '束角式B': '/static/yoga/束角式.png',
      '坐角式A': '/static/yoga/坐角式.png',
      '坐角式B': '/static/yoga/坐角式.png',
      '睡角式': '/static/yoga/卧角式.png',
      '仰卧手抓脚式': '/static/yoga/仰卧抓脚.png',
      '直立手抓脚伸展式A': '/static/yoga/直立抓脚.png',
      '直立手抓脚伸展式B': '/static/yoga/直立抓脚.png',
      '坐立脸朝上加强伸展式A': '/static/yoga/坐立伸展.png',
      '坐立脸朝上加强伸展式B': '/static/yoga/坐立伸展.png',
      '桥式': '/static/yoga/桥.png',

      // 后弯体式
      '轮式': '/static/yoga/轮.png',
      '西方背部伸展': '/static/yoga/西方伸展.png',

      // 结束体式
      '肩倒立': '/static/yoga/肩倒立.png',
      '犁式': '/static/yoga/犁式.png',
      '膝碰耳式': '/static/yoga/膝碰耳.png',
      '脸朝上莲花式': '/static/yoga/倒立莲花.png',
      '胚胎式': '/static/yoga/胚胎式.png',
      '鱼式': '/static/yoga/鱼式.png',
      '莲花鱼式': '/static/yoga/鱼式.png',
      '头肘倒立': '/static/yoga/头肘倒立.png',
      '瑜伽身印': '/static/yoga/瑜伽身印.png',
      '莲花式': '/static/yoga/莲花.png',
      '上提': '/static/yoga/上提.png',
      '摊尸式': '/static/yoga/摊尸.png',

      // 兼容旧名称和别名
      '手杖式': '/static/yoga/手杖.png',
      '三角式': '/static/yoga/三角式.png',
      '侧角式': '/static/yoga/侧角.png',
      '双角式': '/static/yoga/双角.png',
      '西方伸展': '/static/yoga/西方伸展.png',
      '东方伸展': '/static/yoga/东方伸展.png',
      '头碰膝式': '/static/yoga/头碰膝ABC.png',
      '玛利奇式': '/static/yoga/玛利奇ABCD.png',
      '束角式': '/static/yoga/束角式.png',
      '坐角式': '/static/yoga/坐角式.png',
      '并腿延展': '/static/yoga/并腿延展式.png',
      '完全鱼式': '/static/yoga/鱼式.png',
      'Utplutih': '/static/yoga/上提.png',
      '上体': '/static/yoga/上提.png'
    };

    return imageMap[poseName] || null;
  };

  const feelingTranslations = {
    peaceful: '平静',
    energized: '充满活力',
    challenging: '有挑战性',
    grounded: '扎根',
    focused: '专注'
  };

  const feelingEmojis = {
    peaceful: '🧘‍♀️',
    energized: '⚡',
    challenging: '💪',
    grounded: '🌱',
    focused: '🎯'
  };

  // Calculate pose aggregates
  const poseAggregates = useMemo(() => {
    const poseData = {};

    reflectionsData.forEach(reflection => {
      // 兼容不同的字段名称
      const focusedPoses = reflection.focused_poses || reflection.focusedPoses || [];
      console.log('🔍 处理体式反思:', reflection.id, '体式:', focusedPoses);

      if (focusedPoses && focusedPoses.length > 0) {
        focusedPoses.forEach(poseItem => {
          // 处理体式数据，可能是字符串或对象
          let poseName;
          if (typeof poseItem === 'string') {
            poseName = poseItem;
          } else if (typeof poseItem === 'object') {
            // 支持多种字段名称
            poseName = poseItem.chinese_name || poseItem.name || poseItem.chineseName;
          } else {
            console.warn('体式反思数据中未知的体式格式:', poseItem);
            return;
          }

          console.log('🔍 提取到体式名称:', poseName);

          if (!poseData[poseName]) {
            poseData[poseName] = {
              name: poseName,
              comments: [],
              practitioners: new Set(),
              totalSessions: 0,
              avgEnergy: 0,
              avgFocus: 0,
              feelings: {}
            };
          }
          
          // 处理用户数据，确保是字符串
          const userName = typeof reflection.user === 'object' ?
            (reflection.user.name || reflection.user.username || '匿名用户') :
            reflection.user || '匿名用户';

          // 兼容不同的字段名称
          const comment = {
            user: userName,
            content: reflection.content,
            feeling: reflection.feeling,
            energy: reflection.energy_level || reflection.energy || 0,
            focus: reflection.focus_level || reflection.focus || 0,
            flexibility: reflection.flexibility_level || reflection.flexibility || 0,
            date: reflection.date || reflection.created_at
          };

          console.log('🔍 添加评论:', comment);
          poseData[poseName].comments.push(comment);

          poseData[poseName].practitioners.add(userName);
          poseData[poseName].totalSessions++;
          poseData[poseName].avgEnergy += reflection.energy_level || reflection.energy || 0;
          poseData[poseName].avgFocus += reflection.focus_level || reflection.focus || 0;

          poseData[poseName].feelings[reflection.feeling] =
            (poseData[poseName].feelings[reflection.feeling] || 0) + 1;
        });
      }
    });
    
    Object.values(poseData).forEach(pose => {
      pose.practitionerCount = pose.practitioners.size;
      pose.avgEnergy = (pose.avgEnergy / pose.totalSessions).toFixed(1);
      pose.avgFocus = (pose.avgFocus / pose.totalSessions).toFixed(1);
      pose.topFeeling = Object.keys(pose.feelings).reduce((a, b) => 
        pose.feelings[a] > pose.feelings[b] ? a : b, 'peaceful');
      pose.comments.sort((a, b) => new Date(b.date) - new Date(a.date));
    });
    
    // 根据选中的分类过滤体式
    const allPoses = Object.values(poseData).sort((a, b) => b.totalSessions - a.totalSessions);
    const categoryPoses = poseCategories[selectedPoseCategory];

    // 调试信息
    console.log('🔍 当前选中分类:', selectedPoseCategory);
    console.log('🔍 体式分类数据:', poseCategories);
    console.log('🔍 当前分类的体式:', categoryPoses);

    // 确保 categoryPoses 是数组
    if (!Array.isArray(categoryPoses)) {
      console.warn('⚠️ categoryPoses 不是数组:', categoryPoses, '分类:', selectedPoseCategory);
      console.warn('⚠️ 可用分类:', Object.keys(poseCategories || {}));
      return []; // 如果不是数组，返回空数组
    }

    // 为了确保所有定义的体式都显示，即使没有练习数据也要显示
    const filteredPoses = [];

    categoryPoses.forEach(poseItem => {
      // 处理体式数据，可能是字符串或对象
      let poseName;
      if (typeof poseItem === 'string') {
        poseName = poseItem;
      } else if (typeof poseItem === 'object') {
        // 优先使用 chineseName（config.json格式），然后是 chinese_name（数据库格式），最后是 name
        poseName = poseItem.chineseName || poseItem.chinese_name || poseItem.name;
      } else {
        console.warn('未知的体式数据格式:', poseItem);
        return;
      }

      const existingPose = allPoses.find(pose =>
        (pose.chineseName || pose.chinese_name || pose.name) === poseName
      );
      if (existingPose) {
        filteredPoses.push(existingPose);
      } else {
        // 创建一个空的体式数据，确保所有定义的体式都能显示
        filteredPoses.push({
          name: poseName,
          chineseName: poseName,
          comments: [],
          practitioners: new Set(),
          totalSessions: 0,
          avgEnergy: 0,
          avgFocus: 0,
          feelings: {},
          topFeeling: 'peaceful'
        });
      }
    });

    return filteredPoses;
  }, [selectedPoseCategory, reflectionsData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric'
    });
  };

  // 加载状态
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-emerald-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <span className="text-white text-3xl">🧘‍♀️</span>
          </div>
          <h2 className="text-2xl font-light text-emerald-900 mb-2">正在加载体式数据...</h2>
          <p className="text-emerald-600">请稍候，正在从服务器获取最新数据</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-emerald-50/20 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-red-500 text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-light text-red-900 mb-4">数据加载失败</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-emerald-50/20">
      {/* Hero Section with All Poses Overview */}
      <div className="relative bg-gradient-to-r from-emerald-100/50 to-amber-100/30 rounded-3xl mb-12 p-8">
        {/* All Poses Overview Image - Full Display */}
        <div className="relative mb-8">
          <div className="bg-white rounded-2xl p-4 shadow-lg">
            <YogaPoseImage
              src="/static/yoga/All.png"
              alt="阿斯汤加瑜伽所有体式总览"
              poseName="阿斯汤加瑜伽完整序列总览"
              className="w-full h-auto object-contain"
              style={{ maxHeight: 'none' }}
            />
          </div>
          <div className="text-center mt-4">
            <p className="text-sm text-emerald-600/80 font-medium">
              阿斯汤加瑜伽完整序列总览 (1594×126)
            </p>
          </div>
        </div>

        {/* Content Section */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full mb-6 shadow-lg">
            <span className="text-3xl">🧘‍♀️</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-light text-emerald-900 mb-4 tracking-wide">
            阿斯汤加爱好者社区
          </h1>
          <p className="text-lg text-emerald-700/80 mb-8 max-w-3xl mx-auto leading-relaxed">
            和瑜伽、阿斯汤加爱好者一起记录练习，分享体式中的真实感受与体会
          </p>
          <button
            onClick={() => setActiveView('add-reflection')}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            <span className="mr-2">✨</span>
            分享您的练习体验
          </button>
        </div>
      </div>

      {/* Advertisement Carousel */}
      <div className="relative mb-12">
        <div className="bg-white rounded-2xl p-4 shadow-lg overflow-hidden">
          <div className="relative h-[220px] sm:h-[300px] lg:h-[400px] overflow-hidden rounded-xl bg-white">
            <div
              className="flex h-full transition-transform duration-700 ease-in-out will-change-transform"
              style={{ transform: `translateX(-${currentAdIndex * 100}%)` }}
            >
              {adImages.map((imageSrc, index) => (
                <div
                  key={imageSrc}
                  className="w-full flex-shrink-0 flex items-center justify-center px-3"
                >
                  <YogaPoseImage
                    src={imageSrc}
                    alt={`广告 ${index + 1}`}
                    poseName={`广告图片 ${index + 1}`}
                    className="max-w-full max-h-full object-contain"
                    showDebugInfo={true}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Indicators */}
          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {adImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentAdIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentAdIndex
                    ? 'bg-emerald-500 shadow-lg'
                    : 'bg-emerald-200 hover:bg-emerald-300'
                }`}
              />
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => setCurrentAdIndex((prev) => (prev - 1 + adImages.length) % adImages.length)}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-emerald-500/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-emerald-600 transition-all duration-300 shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => setCurrentAdIndex((prev) => (prev + 1) % adImages.length)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-emerald-500/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-emerald-600 transition-all duration-300 shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Pose Gallery */}
      <div className="space-y-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-light text-emerald-900 mb-6">阿斯汤加体式图鉴</h2>
          <p className="text-emerald-700/70 mb-8">每个体式都有独特的图片展示和练习者的真实感受分享</p>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categoryTabs.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedPoseCategory(category)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedPoseCategory === category
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg transform scale-105'
                    : 'bg-white/70 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 hover:border-emerald-300'
                }`}
              >
                {category}
                <span className="ml-2 text-xs opacity-75">
                  ({poseCategories[category]?.length || 0})
                </span>
              </button>
            ))}
          </div>
        </div>

        {poseAggregates.length > 0 ? (
          <div className="space-y-12">
            {poseAggregates.map((pose) => (
              <div key={pose.chineseName || pose.chinese_name || pose.name} className="group">
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 border border-emerald-100/50">
                  {/* Side by Side Layout: Pose Info | User Comments */}
                  <div className="grid lg:grid-cols-2 gap-0" style={{minHeight: 'auto'}}>

                    {/* LEFT SIDE - Pose Information */}
                    <div className="relative flex flex-col">
                      {/* Pose Image - 保持比例，尽可能放大 */}
                      <div className="relative bg-white flex items-center justify-center p-2" style={{minHeight: '300px'}}>
                        <YogaPoseImage
                          src={getPoseImage(pose, pose.chineseName || pose.chinese_name || pose.name)}
                          alt={pose.chineseName || pose.chinese_name || pose.name}
                          poseName={pose.chineseName || pose.chinese_name || pose.name}
                          className="object-contain transition-transform duration-700 group-hover:scale-105"
                          style={{
                            width: 'auto',
                            height: 'auto',
                            maxWidth: '95%',
                            maxHeight: '280px'
                          }}
                          showDebugInfo={true}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent pointer-events-none"></div>
                        <div className="absolute bottom-2 left-2">
                          <h3 className="text-sm font-bold mb-1 text-white drop-shadow-lg" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.8)'}}>{pose.chineseName || pose.chinese_name || pose.name}</h3>
                          <div className="flex items-center space-x-1 text-xs text-white drop-shadow-md" style={{textShadow: '1px 1px 2px rgba(0,0,0,0.8)'}}>
                            <span className="flex items-center bg-white/20 px-2 py-1 rounded-full">
                              <span className="mr-1">👥</span>
                              {pose.practitionerCount}
                            </span>
                            <span className="flex items-center bg-white/20 px-2 py-1 rounded-full">
                              <span className="mr-1">📝</span>
                              {pose.totalSessions}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Pose Stats - 压缩到1/3大小 */}
                      <div className="p-2 bg-gradient-to-br from-emerald-50/50 to-teal-50/30">
                        <h4 className="text-sm font-medium text-emerald-800 mb-2">练习数据</h4>
                        <div className="grid grid-cols-3 gap-1 mb-2">
                          <div className="text-center p-1 bg-white/60 rounded border border-emerald-100">
                            <div className="text-sm font-medium text-emerald-700">{pose.avgEnergy}</div>
                            <div className="text-xs text-emerald-600">能量</div>
                          </div>
                          <div className="text-center p-1 bg-white/60 rounded border border-amber-100">
                            <div className="text-sm font-medium text-amber-700">{pose.avgFocus}</div>
                            <div className="text-xs text-amber-600">专注</div>
                          </div>
                          <div className="text-center p-1 bg-white/60 rounded border border-teal-100">
                            <div className="text-sm">{feelingEmojis[pose.topFeeling]}</div>
                            <div className="text-xs text-teal-600">感受</div>
                          </div>
                        </div>
                        <div className="text-center">
                          <span className="inline-flex items-center px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                            {feelingTranslations[pose.topFeeling]}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT SIDE - User Comments */}
                    <div className="p-4 bg-gradient-to-br from-stone-50/50 to-emerald-50/20 flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-medium text-emerald-800">练习者分享</h4>
                        <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                          {pose.comments && pose.comments.length} 条
                        </span>
                      </div>

                      {/* Comments List */}
                      <div className="flex-1 overflow-y-auto max-h-48 space-y-3 pr-2">
                        {pose.comments && pose.comments.length > 0 ? (
                          <>
                            {pose.comments.slice(0, expandedPose === (pose.chineseName || pose.chinese_name || pose.name) ? pose.comments.length : 3).map((comment, index) => (
                              <div key={index} className="bg-white/70 rounded-lg p-3 border border-emerald-100/50 hover:shadow-md transition-shadow duration-200">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-7 h-7 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-medium text-xs">
                                      {comment.user.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <div className="font-medium text-emerald-800 text-xs">{comment.user}</div>
                                      <div className="text-xs text-emerald-600">{formatDate(comment.date)}</div>
                                    </div>
                                  </div>
                                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                                    {feelingEmojis[comment.feeling]}
                                  </span>
                                </div>

                                <blockquote className="text-xs text-gray-700 leading-relaxed italic mb-2 pl-3 border-l-2 border-emerald-200">
                                  "{comment.content}"
                                </blockquote>

                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2 text-xs text-emerald-600">
                                    <span className="flex items-center bg-emerald-50 px-2 py-1 rounded-full">
                                      <span className="mr-1">⚡</span>
                                      {comment.energy}/10
                                    </span>
                                    <span className="flex items-center bg-amber-50 px-2 py-1 rounded-full">
                                      <span className="mr-1">🎯</span>
                                      {comment.focus}/10
                                    </span>
                                    {comment.flexibility && (
                                      <span className="flex items-center bg-teal-50 px-2 py-1 rounded-full">
                                        <span className="mr-1">🌿</span>
                                        {comment.flexibility}/10
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className="text-center py-6 text-emerald-600/70">
                            <div className="text-3xl mb-2">🧘‍♀️</div>
                            <p className="text-sm">还没有练习分享</p>
                            <p className="text-xs mt-1 text-emerald-500">
                              {pose.chineseName || pose.chinese_name || pose.name} - 成为第一个分享这个体式的人
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Show More/Less Button */}
                      {pose.comments && pose.comments.length > 3 && (
                        <div className="mt-4 pt-4 border-t border-emerald-100/50">
                          <button
                            onClick={() => {
                              const poseName = pose.chineseName || pose.chinese_name || pose.name;
                              setExpandedPose(expandedPose === poseName ? null : poseName);
                            }}
                            className="w-full py-2 text-sm text-emerald-600 hover:text-emerald-700 transition-colors duration-200 flex items-center justify-center space-x-2 hover:bg-emerald-50/50 rounded-lg"
                          >
                            <span>
                              {expandedPose === (pose.chineseName || pose.chinese_name || pose.name)
                                ? '收起评论'
                                : `查看全部 ${pose.comments.length} 条分享`
                              }
                            </span>
                            <svg className={`w-4 h-4 transition-transform duration-200 ${expandedPose === (pose.chineseName || pose.chinese_name || pose.name) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">🧘‍♀️</span>
            </div>
            <h3 className="text-xl font-light text-emerald-800 mb-2">开始您的瑜伽之旅</h3>
            <p className="text-emerald-600/70 mb-6">还没有体式练习记录，成为第一个分享者吧</p>
            <button 
              onClick={() => setActiveView('add-reflection')}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full hover:from-emerald-600 hover:to-teal-700 transition-all duration-300"
            >
              开始记录练习
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 
